import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  matchEmailPatient,
} from "../../../utils/api";
import EmailPatientPicker, { patientIdOf, patientName } from "./EmailPatientPicker";
import {
  formatEmailAddress,
  getErrorMessage,
} from "./emailFormatters";

const participantLabel = (participant) => {
  if (!participant) return "";
  const name = patientName(participant);
  const account = participant.patientAccountNumber ? `MRN ${participant.patientAccountNumber}` : "No MRN";
  return `${name} (${account})`;
};

const EmailPatientMatchDialog = ({ email, open, onClose, onMatched }) => {
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const emailAddress = useMemo(() => {
    if (!email) return "";
    return email.direction === "INBOUND"
      ? formatEmailAddress(email.fromAddress)
      : formatEmailAddress(email.toAddress);
  }, [email]);

  useEffect(() => {
    if (!open) return;
    setSelectedParticipant(null);
    setError("");
  }, [open]);

  const handleMatch = async () => {
    const selectedPatientId = patientIdOf(selectedParticipant);
    if (!email?.emailId || !selectedPatientId) return;
    setSaving(true);
    setError("");
    try {
      const response = await matchEmailPatient(email.emailId, selectedPatientId);
      onMatched?.(response?.data || response, selectedParticipant);
      setConfirmOpen(false);
      onClose();
    } catch (err) {
      console.error("Failed to match email participant:", err);
      setError(getErrorMessage(err, "Failed to match participant."));
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>Match Participant</DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Email
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                {emailAddress || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                Subject
              </Typography>
              <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                {email?.subject || "N/A"}
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ color: "text.secondary", fontWeight: 700, mb: -1 }}>
              Participant
            </Typography>
            <Box
              sx={{
                border: "1px solid #d0d0d0",
                borderRadius: 0,
              }}
            >
              <EmailPatientPicker
                selectedPatientId={patientIdOf(selectedParticipant)}
                selectedScope={selectedParticipant ? "patient" : ""}
                onSelectPatient={(patient) => setSelectedParticipant(patient)}
                includeContact
                showContact={false}
                showPhone={false}
                compact={true}
                showHeader={false}
                height={360}
              />
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedParticipant || saving}
            onClick={() => setConfirmOpen(true)}
          >
            Match
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Match</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Match this email to <strong>{participantLabel(selectedParticipant)}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleMatch} disabled={saving}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailPatientMatchDialog;
